import {
  STATIONS,
  ROUTES,
  MAP_VIEWBOX,
  RAIL_Y,
  RAIL_X_START,
  RAIL_X_END,
  SUBWAY_CORNER_RADIUS,
  type StationId,
  type RouteId,
  type Route,
} from "./registry";

interface Props {
  /** Station the user is currently "at" — derived from currentTab + most-recent substation. */
  youAreHereStationId: StationId;
  /** Currently-hovered station, or null. Hover wins over standing-position for focus. */
  hoveredStationId: StationId | null;
  /** Hover callback — pass null on mouse leave. */
  onHoverStation: (id: StationId | null) => void;
  /** Routes whose backend cycle has been enacted — solid-colored even when not touching focus. */
  enactedRouteIds: ReadonlySet<RouteId>;
}

const TAB_BANDS = [
  { tab: "FRAME",   color: "var(--chart-2)",   x: 40,  w: 80 },
  { tab: "CURATE",  color: "var(--chart-1)",   x: 170, w: 270 },
  { tab: "ANALYZE", color: "var(--chart-4)",   x: 680, w: 100 },
  { tab: "BUILD",   color: "var(--chart-5)",   x: 794, w: 76 },
] as const;

export function SubwayMap({
  youAreHereStationId,
  hoveredStationId,
  onHoverStation,
  enactedRouteIds,
}: Props) {
  const focusStation: StationId = hoveredStationId ?? youAreHereStationId;
  const xOf = (id: StationId) => STATIONS.find((s) => s.id === id)!.x;

  return (
    <svg viewBox={`0 0 ${MAP_VIEWBOX.w} ${MAP_VIEWBOX.h}`} width="100%" style={{ display: "block", overflow: "visible" }}>
      {/* Tab bands below the rail */}
      {TAB_BANDS.map((b) => (
        <g key={b.tab}>
          <rect
            x={b.x}
            y={RAIL_Y + 200}
            width={b.w}
            height={24}
            rx={5}
            fill={b.color}
            fillOpacity={0.12}
            stroke={b.color}
            strokeOpacity={0.4}
          />
          <text
            x={b.x + b.w / 2}
            y={RAIL_Y + 216}
            textAnchor="middle"
            fontSize="11"
            fontWeight={600}
            fontFamily="JetBrains Mono, monospace"
            letterSpacing="0.16em"
            fill={b.color}
          >
            {b.tab}
          </text>
        </g>
      ))}

      {/* MIND THE GAP annotation between CURATE and ANALYZE bands */}
      <g style={{ opacity: 0.65 }}>
        <line x1={460} y1={RAIL_Y + 228} x2={660} y2={RAIL_Y + 228} stroke="var(--chart-5)" strokeOpacity={0.35} strokeWidth={1} strokeDasharray="3 4" />
        <path d={`M 460 ${RAIL_Y + 224} L 460 ${RAIL_Y + 232}`} stroke="var(--chart-5)" strokeOpacity={0.5} strokeWidth={1} />
        <path d={`M 660 ${RAIL_Y + 224} L 660 ${RAIL_Y + 232}`} stroke="var(--chart-5)" strokeOpacity={0.5} strokeWidth={1} />
        <text x={560} y={RAIL_Y + 240} textAnchor="middle" fontSize="9.5" fontFamily="JetBrains Mono, monospace" letterSpacing="0.22em" fontStyle="italic" fontWeight={600} fill="var(--chart-5)">
          MIND THE (lit) GAP
        </text>
      </g>

      {/* The 6-phase rail */}
      <line x1={RAIL_X_START} y1={RAIL_Y} x2={RAIL_X_END} y2={RAIL_Y} stroke="var(--border)" strokeWidth={12} strokeLinecap="round" />
      <line x1={RAIL_X_START} y1={RAIL_Y} x2={RAIL_X_END} y2={RAIL_Y} stroke="var(--surface-crust)" strokeWidth={1.5} strokeDasharray="4 7" />

      {/* Routes (subway tracks) */}
      {ROUTES.map((route) => (
        <RouteTrack
          key={route.id}
          route={route}
          xOf={xOf}
          focusStation={focusStation}
          enacted={enactedRouteIds.has(route.id)}
        />
      ))}

      {/* Stations */}
      {STATIONS.map((s) => (
        <StationNode
          key={s.id}
          station={s}
          isStanding={s.id === youAreHereStationId}
          isHovered={s.id === hoveredStationId}
          onHover={onHoverStation}
        />
      ))}
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface RouteTrackProps {
  route: Route;
  xOf: (id: StationId) => number;
  focusStation: StationId;
  enacted: boolean;
}

function RouteTrack({ route, xOf, focusStation, enacted }: RouteTrackProps) {
  const x1 = xOf(route.from);
  const x2 = xOf(route.to);
  const trackY = RAIL_Y + route.yOffset;
  const isAbove = route.yOffset < 0;
  const direction = x2 > x1 ? 1 : -1;
  const railEdge = RAIL_Y + (isAbove ? -16 : 16);
  const cornerSign = isAbove ? 1 : -1;
  const cr = SUBWAY_CORNER_RADIUS;
  const path = [
    `M ${x1} ${railEdge}`,
    `L ${x1} ${trackY + cornerSign * cr}`,
    `Q ${x1} ${trackY}, ${x1 + direction * cr} ${trackY}`,
    `L ${x2 - direction * cr} ${trackY}`,
    `Q ${x2} ${trackY}, ${x2} ${trackY + cornerSign * cr}`,
    `L ${x2} ${railEdge}`,
  ].join(" ");

  const touchesFocus = route.from === focusStation || route.to === focusStation;
  const strokeColor = touchesFocus || enacted ? route.color : "var(--border)";
  const strokeOpacity = touchesFocus || enacted ? 1 : 0.55;
  const strokeWidth = touchesFocus ? 2.6 : 2;
  const dashed = !touchesFocus && !enacted;
  const showLabel = touchesFocus;

  const baseMidX = (x1 + direction * cr + (x2 - direction * cr)) / 2;
  const labelX = baseMidX + (route.labelXOffset ?? 0);
  const labelY = isAbove ? trackY - 6 : trackY + 15;

  return (
    <g opacity={strokeOpacity}>
      <path
        data-route-id={route.id}
        d={path}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeDasharray={dashed ? "4 4" : undefined}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {showLabel && (
        <text
          x={labelX}
          y={labelY}
          textAnchor="middle"
          fontSize="13"
          fontFamily="Outfit, sans-serif"
          fontWeight={500}
          fill={strokeColor}
        >
          {route.label}
        </text>
      )}
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface StationNodeProps {
  station: (typeof STATIONS)[number];
  isStanding: boolean;
  isHovered: boolean;
  onHover: (id: StationId | null) => void;
}

function StationNode({ station, isStanding, isHovered, onHover }: StationNodeProps) {
  const isTransit = !!station.transit;
  const ringR = isTransit ? 10 : 14;
  const innerR = isTransit ? 4 : 5.5;
  const labelFs = isTransit ? 12 : 14;
  const labelDy = isTransit ? 30 : 36;

  return (
    <g
      style={{ cursor: "pointer" }}
      onMouseEnter={() => onHover(station.id)}
      onMouseLeave={() => onHover(null)}
    >
      {isStanding && (
        <circle cx={station.x} cy={RAIL_Y} r={26} fill={station.color} fillOpacity={0.18} />
      )}
      {isHovered && !isStanding && (
        <circle
          cx={station.x}
          cy={RAIL_Y}
          r={ringR + 8}
          fill="none"
          stroke="var(--foreground)"
          strokeOpacity={0.55}
          strokeWidth={1.5}
          strokeDasharray="2 3"
        />
      )}
      <circle
        cx={station.x}
        cy={RAIL_Y}
        r={ringR}
        fill={isHovered && !isStanding ? "var(--muted)" : "var(--surface-crust)"}
        stroke={isHovered && !isStanding ? "var(--foreground)" : station.color}
        strokeWidth={isStanding ? 3.5 : 2}
        strokeDasharray={isTransit ? "3 3" : ""}
      />
      <circle cx={station.x} cy={RAIL_Y} r={innerR} fill={station.color} fillOpacity={isTransit ? 0.6 : 1} />
      <text
        x={station.x}
        y={RAIL_Y + labelDy}
        textAnchor="middle"
        fontSize={labelFs}
        fontWeight={isStanding ? 700 : isTransit ? 500 : 600}
        fontStyle={isTransit ? "italic" : "normal"}
        fontFamily="Outfit, sans-serif"
        fill={isTransit ? "var(--text-soft)" : "var(--foreground)"}
      >
        {station.label}
      </text>
      {isStanding && (
        <g>
          <rect
            x={station.x - 48}
            y={RAIL_Y - 58}
            width={96}
            height={20}
            rx={10}
            fill={station.color}
          />
          <text
            x={station.x}
            y={RAIL_Y - 44}
            textAnchor="middle"
            fontSize="10"
            fontWeight={700}
            fill="var(--surface-crust)"
            fontFamily="Outfit, sans-serif"
            letterSpacing="0.06em"
          >
            YOU ARE HERE
          </text>
          <path d={`M ${station.x} ${RAIL_Y - 38} l -5 -5 l 10 0 z`} fill={station.color} />
        </g>
      )}
    </g>
  );
}
